import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { PageHeader } from '@/components/UI/PageHeader';
import { SearchInput } from '@/components/UI/SearchInput';
import { StatCard } from '@/components/UI/StatCard';
import { Chip } from '@/components/UI/Chip';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Tabs } from '@/components/UI/Tabs';
import { Pagination } from '@/components/UI/Pagination';
import { Alert } from '@/components/UI/Alert';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { getErrorDetail } from '@/utils/apiError';
import {
  useListPlatformUsersQuery,
  useUpdatePlatformUserStatusMutation,
  useCreatePlatformUserMutation,
  useDeletePlatformUserMutation,
  useListPlatformTenantsQuery,
  useListPlatformRolesQuery,
  PlatformUser,
  CreatePlatformUserRequest,
} from '@/redux/internal-services/platformAdminApi';

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  active:    'success',
  invited:   'accent',
  inactive:  'neutral',
  suspended: 'warning',
  deleted:   'danger',
};

const STATUSES = ['All', 'active', 'invited', 'inactive', 'suspended'];

const PLATFORM_ROLES = [
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'platform_support', label: 'Platform Support' },
];

const Avatar: React.FC<{ name?: string | null; url?: string | null }> = ({ name, url }) => {
  if (url) return <img src={url} alt={name ?? ''} className="h-8 w-8 rounded-full object-cover" />;
  const initials = (name ?? '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="h-8 w-8 rounded-full bg-accent-soft flex items-center justify-center text-accent-text text-xs font-semibold flex-shrink-0">
      {initials}
    </div>
  );
};

// ── Add User Modal ────────────────────────────────────────────────────────────

interface AddUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreatePlatformUserRequest>({
    name: '',
    email: '',
    password: '',
    platform_role: null,
    tenant_id: null,
    tenant_role_ids: [],
  });
  const [assignType, setAssignType] = useState<'platform' | 'tenant'>('platform');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const { data: tenantsData } = useListPlatformTenantsQuery();
  const tenants = tenantsData?.data ?? [];

  const { data: rolesData } = useListPlatformRolesQuery(
    { tenantId: form.tenant_id ?? '' },
    { skip: assignType !== 'tenant' || !form.tenant_id }
  );
  const tenantRoles = rolesData?.data ?? [];

  const [createUser, { isLoading }] = useCreatePlatformUserMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setError(t('platform.userFormRequiredError'));
      return;
    }
    if (assignType === 'platform' && !form.platform_role) {
      setError(t('platform.selectPlatformRole'));
      return;
    }
    if (assignType === 'tenant' && !form.tenant_id) {
      setError(t('platform.selectTenant'));
      return;
    }
    // Resolve tenant admin role UUID for the selected tenant
    const tenantAdminRoleId = tenantRoles.find((r) =>
      r.name === 'Tenant Admin' || r.role_id === 'tenant_admin'
    )?.role_id ?? null;
    if (assignType === 'tenant' && !tenantAdminRoleId) {
      setError(t('platform.tenantAdminRoleResolutionFailed'));
      return;
    }
    try {
      await createUser({
        ...form,
        platform_role: assignType === 'platform' ? form.platform_role : null,
        tenant_id: assignType === 'tenant' ? form.tenant_id : null,
        tenant_role_ids: assignType === 'tenant' && tenantAdminRoleId ? [tenantAdminRoleId] : [],
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? 'Failed to create user.');
    }
  };

  return (
    <Modal
      title="Add User"
      onClose={onClose}
      maxWidthClassName="max-w-md"
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" size="sm" type="button" onClick={onClose}>Cancel</Button>
          <Button size="sm" type="submit" form="add-user-form" loading={isLoading}>Create User</Button>
        </>
      }
    >
    <form id="add-user-form" onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-ui-text2 mb-1">Full Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={fieldClass()}
          placeholder="John Smith"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-ui-text2 mb-1">Email</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={fieldClass()}
          placeholder="user@example.com"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-ui-text2 mb-1">Password</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={cn(fieldClass(), 'pr-10')}
            placeholder="Min. 6 characters"
          />
          <IconButton
            size="xs"
            label="Hold to show password"
            onMouseDown={() => setShowPassword(true)}
            onMouseUp={() => setShowPassword(false)}
            onMouseLeave={() => setShowPassword(false)}
            onTouchStart={() => setShowPassword(true)}
            onTouchEnd={() => setShowPassword(false)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            tabIndex={-1}
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </IconButton>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-ui-text2 mb-2">Assign As</label>
        <Tabs
          variant="segmented"
          label="Assign as"
          tabs={[
            { id: 'platform', label: 'Platform Role' },
            { id: 'tenant', label: 'Tenant Admin' },
          ]}
          value={assignType}
          onChange={(id) => setAssignType(id as 'platform' | 'tenant')}
        />
      </div>

      {assignType === 'platform' && (
        <div>
          <label className="block text-xs font-medium text-ui-text2 mb-1">Platform Role</label>
          <select
            value={form.platform_role ?? ''}
            onChange={(e) => setForm({ ...form, platform_role: e.target.value || null })}
            className={fieldClass()}
          >
            <option value="">Select role...</option>
            {PLATFORM_ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      )}

      {assignType === 'tenant' && (
        <div>
          <label className="block text-xs font-medium text-ui-text2 mb-1">Tenant</label>
          <select
            value={form.tenant_id ?? ''}
            onChange={(e) => setForm({ ...form, tenant_id: e.target.value || null })}
            className={fieldClass()}
          >
            <option value="">Select tenant...</option>
            {tenants.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.company_name}</option>
            ))}
          </select>
          <p className="text-xs text-ui-text3 mt-1">User will be assigned as Tenant Admin.</p>
        </div>
      )}

      {error && <Alert tone="danger">{error}</Alert>}
    </form>
    </Modal>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const LIMIT = 50;

type SortField = 'name' | 'tenant_name' | 'status' | 'created_at';
type SortDir = 'asc' | 'desc';

const SortIcon: React.FC<{ field: SortField; current: SortField; dir: SortDir }> = ({ field, current, dir }) => {
  if (field !== current) return <span className="ml-1 text-ui-text3">↕</span>;
  return <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>;
};

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [confirmUser, setConfirmUser] = useState<PlatformUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<'suspend' | 'activate' | 'delete' | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [msg, setMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => { setPage(1); }, [status]);

  const { data, isLoading, isFetching, refetch } = useListPlatformUsersQuery(
    { search: debounced || undefined, status: status || undefined, page, limit: LIMIT },
    { refetchOnMountOrArgChange: true }
  );
  const [updateStatus, { isLoading: saving }] = useUpdatePlatformUserStatusMutation();
  const [deleteUser, { isLoading: deleting }] = useDeletePlatformUserMutation();

  const rawUsers: PlatformUser[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const users = [...rawUsers].sort((a, b) => {
    let av: string, bv: string;
    if (sortField === 'name') { av = (a.name ?? '').toLowerCase(); bv = (b.name ?? '').toLowerCase(); }
    else if (sortField === 'tenant_name') { av = (a.tenant_name ?? a.tenant_id ?? '').toLowerCase(); bv = (b.tenant_name ?? b.tenant_id ?? '').toLowerCase(); }
    else if (sortField === 'status') { av = a.status; bv = b.status; }
    else { av = a.created_at ?? ''; bv = b.created_at ?? ''; }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleConfirm = async () => {
    if (!confirmUser || !confirmAction) return;
    setMsg(null);
    try {
      if (confirmAction === 'delete') {
        await deleteUser(confirmUser.user_id).unwrap();
        setMsg({ type: 'success', text: `User "${confirmUser.name ?? confirmUser.email}" has been deleted.` });
      } else {
        const newStatus = confirmAction === 'suspend' ? 'suspended' : 'active';
        await updateStatus({ userId: confirmUser.user_id, status: newStatus }).unwrap();
        setMsg({ type: 'success', text: `User status updated to "${newStatus}".` });
      }
      setConfirmUser(null);
      setConfirmAction(null);
      refetch();
    } catch (err: unknown) {
      setMsg({ type: 'error', text: getErrorDetail(err) ?? 'Action failed.' });
      setConfirmUser(null);
      setConfirmAction(null);
    }
  };

  const actionLabel = confirmAction === 'delete' ? 'Delete' : confirmAction === 'suspend' ? 'Suspend' : 'Activate';

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="User Management"
          subtitle="View and manage all users across the platform."
          actions={
            <Button onClick={() => setShowAddModal(true)}>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </Button>
          }
          toolbar={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
              />
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <Chip
                    key={s}
                    className="capitalize"
                    selected={s === 'All' ? status === '' : status === s}
                    onClick={() => setStatus(s === 'All' ? '' : s)}
                  >
                    {s}
                  </Chip>
                ))}
              </div>
            </div>
          }
        />
      }
    >

      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Users" value={isLoading ? '—' : total} tone="accent" />
        <StatCard label="Active" value={isLoading ? '—' : users.filter((u) => u.status === 'active').length} tone="success" />
        <StatCard label="Invited" value={isLoading ? '—' : users.filter((u) => u.status === 'invited').length} tone="accent" />
        <StatCard label="Inactive" value={isLoading ? '—' : users.filter((u) => u.status === 'inactive' || u.status === 'suspended').length} />
      </div>

      {msg && (
        <Alert tone={msg.type === 'error' ? 'danger' : 'success'} className="mb-4">{msg.text}</Alert>
      )}

      {/* Table */}
      <div className="table-card">
        {isLoading || isFetching ? (
          <LoadingState label="Loading users..." />
        ) : users.length === 0 ? (
          <EmptyState title={t('platform.noUsersFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('name')}>
                    User <SortIcon field="name" current={sortField} dir={sortDir} />
                  </th>
                  <th className="th-cell hidden md:table-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('tenant_name')}>
                    Tenant <SortIcon field="tenant_name" current={sortField} dir={sortDir} />
                  </th>
                  <th className="th-cell hidden lg:table-cell">Roles</th>
                  <th className="th-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('status')}>
                    Status <SortIcon field="status" current={sortField} dir={sortDir} />
                  </th>
                  <th className="th-cell hidden lg:table-cell cursor-pointer select-none hover:text-ui-text" onClick={() => toggleSort('created_at')}>
                    Joined <SortIcon field="created_at" current={sortField} dir={sortDir} />
                  </th>
                  <th className="th-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={`${user.user_id}-${user.tenant_id}`}
                    className="tr-hover"
                    onClick={() => navigate(`/platform/users/${user.user_id}`)}
                  >
                    <td className="td-cell">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} url={user.profile_picture_url} />
                        <div>
                          <p className="text-sm font-medium text-ui-text">{user.name ?? '—'}</p>
                          <p className="text-xs text-ui-text3">{user.email ?? '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="td-cell hidden md:table-cell">
                      <span className="text-sm text-ui-text2">{user.tenant_name ?? user.tenant_id ?? '—'}</span>
                    </td>
                    <td className="td-cell hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.role_names.map((r) => (
                          <Badge key={r} variant="accent">{r}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="td-cell">
                      <Badge variant={STATUS_VARIANT[user.status] ?? 'neutral'} className="capitalize">
                        {user.status}
                      </Badge>
                    </td>
                    <td className="td-cell hidden lg:table-cell">
                      <span className="text-sm text-ui-text3">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="td-cell text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {user.status === 'active' && (
                          <Button
                            variant="outline"
                            size="xs"
                            className="text-warning-text"
                            onClick={() => { setConfirmUser(user); setConfirmAction('suspend'); setMsg(null); }}
                          >
                            Suspend
                          </Button>
                        )}
                        {user.status === 'suspended' && (
                          <Button
                            variant="outline"
                            size="xs"
                            className="text-success-text"
                            onClick={() => { setConfirmUser(user); setConfirmAction('activate'); setMsg(null); }}
                          >
                            Activate
                          </Button>
                        )}
                        {user.status !== 'deleted' && (
                          <Button
                            variant="destructive-ghost"
                            size="xs"
                            onClick={() => { setConfirmUser(user); setConfirmAction('delete'); setMsg(null); }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-ui-text2">
            {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total} users
          </p>
          <Pagination
            className="pt-0"
            page={page - 1}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p + 1)}
          />
        </div>
      )}

      {/* Confirm Modal */}
      {confirmUser && confirmAction && (
        <Modal
          title={`Confirm ${actionLabel}`}
          onClose={() => { setConfirmUser(null); setConfirmAction(null); }}
          onSave={handleConfirm}
          saving={saving || deleting}
          saveLabel={`Confirm ${actionLabel}`}
          danger={confirmAction !== 'activate'}
          maxWidthClassName="max-w-sm"
        >
          <p className="text-sm text-ui-text2">
            {confirmAction === 'delete'
              ? <>Are you sure you want to <strong>delete</strong> <strong>{confirmUser.name ?? confirmUser.email}</strong>? This is a soft delete — history is preserved.</>
              : <>Are you sure you want to <strong>{actionLabel.toLowerCase()}</strong> <strong>{confirmUser.name ?? confirmUser.email}</strong>? This applies globally across all tenants.</>
            }
          </p>
        </Modal>
      )}

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); refetch(); setMsg({ type: 'success', text: 'User created successfully.' }); }}
        />
      )}
    </PageShell>
  );
};

export default UsersPage;
